
import { Plus, X } from "lucide-react";
import type React from "react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import useApiHook from "../../api/Api";
import { toast } from "react-toastify";
import Toast from "../../Toast";
import { useNavigate } from "react-router-dom";
import type { ReportFormData, ReportPayload } from "../../../utils/interface";

const CreateReport: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [formData, setFormData] = useState<ReportFormData>({
    total_sent: "",
    total_delivered: "",
    total_pending: "",
    total_failed: "",
    total_absent_from_network: "",
    invalid: "",
    total_delivered_to_network: "",
    report_date: "",
    sms_class: "",
  });

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { isLoading, makeRequest } = useApiHook<any>("/reports", {
    method: "post",
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = e.target;
    setFormData((prev: ReportFormData) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const { name, value } = e.target;
    setFormData((prev: ReportFormData) => ({
      ...prev,
      [name]: value,
    }));
  };

  const [toasts, setToasts] = useState<{
    message: string;
    type: "success" | "error";
    isVisible: boolean;
  }>({
    message: "",
    type: "success",
    isVisible: false,
  });

  const showToast = (message: string, type: "success" | "error") => {
    setToasts({
      message,
      type,
      isVisible: true,
    });
  };

  const hideToast = () => {
    setToasts((prev) => ({ ...prev, isVisible: false }));
  };

  const handleSubmit = async (
    e: React.FormEvent<HTMLFormElement>
  ): Promise<void> => {
    e.preventDefault();

    const payload: ReportPayload = {
      total_sent: Number.parseInt(formData.total_sent) || 0,
      total_delivered: Number.parseInt(formData.total_delivered) || 0,
      total_pending: Number.parseInt(formData.total_pending) || 0,
      total_failed: Number.parseInt(formData.total_failed) || 0,
      total_absent_from_network:
        Number.parseInt(formData.total_absent_from_network) || 0,
      invalid: Number.parseInt(formData.invalid) || 0,
      total_delivered_to_network:
        Number.parseInt(formData.total_delivered_to_network) || 0,
      report_date: formData.report_date,
      sms_class: formData.sms_class,
    };

    const response = await makeRequest(payload);

    if (response?.status === 201) {
      setIsModalOpen(false);
      setFormData({
        total_sent: "",
        total_delivered: "",
        total_pending: "",
        total_failed: "",
        total_absent_from_network: "",
        invalid: "",
        total_delivered_to_network: "",
        report_date: "",
        sms_class: "",
      });

      queryClient.invalidateQueries({
        queryKey: ["metrics"],
      });

      showToast("Report created successfully! 🎉", "success");
      navigate(`/vas?mode=report`, { replace: true });
      window.location.reload();
    } else {
      toast.warn(response?.data?.message || "An error occurred");
      toast.warning(response?.data?.error || "An error occurred");
    }
  };

  const openModal = (): void => {
    setIsModalOpen(true);
  };

  const closeModal = (): void => {
    setIsModalOpen(false);
  };

  return (
    <section>
      <button
        onClick={openModal}
        className="flex items-center rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
      >
        <Plus className="mr-2 h-4 w-4" />
        Create Report
      </button>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div
            className="fixed inset-0 bg-black bg-opacity-50"
            onClick={closeModal}
          ></div>

          <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 max-h-screen overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="text-xl font-semibold text-gray-800">
                Create New Report
              </h2>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full p-1"
              >
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="total_sent"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Total Sent
                  </label>
                  <input
                    type="number"
                    id="total_sent"
                    name="total_sent"
                    value={formData.total_sent}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="total_delivered"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Total Delivered
                  </label>
                  <input
                    type="number"
                    id="total_delivered"
                    name="total_delivered"
                    value={formData.total_delivered}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="total_pending"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Total Pending
                  </label>
                  <input
                    type="number"
                    id="total_pending"
                    name="total_pending"
                    value={formData.total_pending}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="total_failed"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Total Failed
                  </label>
                  <input
                    type="number"
                    id="total_failed"
                    name="total_failed"
                    value={formData.total_failed}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="total_absent_from_network"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Total Absent from Network
                  </label>
                  <input
                    type="number"
                    id="total_absent_from_network"
                    name="total_absent_from_network"
                    value={formData.total_absent_from_network}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="invalid"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Invalid
                  </label>
                  <input
                    type="number"
                    id="invalid"
                    name="invalid"
                    value={formData.invalid}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="total_delivered_to_network"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Total Delivered to Network
                  </label>
                  <input
                    type="number"
                    id="total_delivered_to_network"
                    name="total_delivered_to_network"
                    value={formData.total_delivered_to_network}
                    onChange={handleInputChange}
                    min="0"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label
                    htmlFor="report_date"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Report Date
                  </label>
                  <input
                    type="date"
                    id="report_date"
                    name="report_date"
                    value={formData.report_date}
                    onChange={handleInputChange}
                    max={
                      new Date(new Date().setDate(new Date().getDate() - 1))
                        .toISOString()
                        .split("T")[0]
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Only past dates are allowed for reporting
                  </p>
                </div>
                <div>
                  <label
                    htmlFor="sms_class"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    SMS Class
                  </label>
                  <select
                    id="sms_class"
                    name="sms_class"
                    value={formData.sms_class}
                    onChange={handleSelectChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  >
                    <option value="">Select SMS Class</option>
                    <option value="transactional">Transactional</option>
                    <option value="otp">OTP</option>
                    <option value="promotion">Promotion</option>
                  </select>
                </div>
              </div>

              <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? "Creating..." : "Create Report"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toast
        message={toasts.message}
        type={toasts.type}
        isVisible={toasts.isVisible}
        onClose={hideToast}
      />
    </section>
  );
};

export default CreateReport;
